import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { getLenis } from "@/hooks/useSmoothScroll";

const links = [
  { label: "Projects", target: "projects" },
  { label: "Practice", target: "practice" },
  { label: "Contact", target: "contact" },
];

export default function Navigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState("");

  const goTo = (target) => {
    if (location.pathname !== "/") {
      navigate(`/#${target}`);
      return;
    }
    const el = document.getElementById(target);
    const lenis = getLenis();
    if (el && lenis) lenis.scrollTo(el, { offset: -40, duration: 1.2 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  // observe sections for active state on home
  useEffect(() => {
    if (location.pathname !== "/") return;
    const ids = links.map((l) => l.target);
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -45% 0px" }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [location.pathname]);

  return (
    <motion.header
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: [0.25, 0.1, 0.25, 1], delay: 0.2 }}
      className="fixed inset-x-0 top-0 z-[500]"
      data-testid="main-nav"
    >
      <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-6 md:px-12 md:py-8">
        <button
          data-testid="nav-logo"
          data-cursor="link"
          onClick={() => {
            if (location.pathname !== "/") navigate("/");
            else getLenis()?.scrollTo(0, { duration: 1.1 });
          }}
          className="flex items-baseline gap-3"
        >
          <span
            className="text-lg font-medium tracking-[0.3em]"
            style={{ color: "#000" }}
          >
            AMA
          </span>
          <span className="hidden text-[0.6rem] uppercase tracking-[0.24em] text-black/40 sm:inline">
            Ante Meridian
          </span>
        </button>

        <nav className="flex items-center gap-7 md:gap-10">
          {links.map((l) => (
            <button
              key={l.target}
              data-testid={`nav-${l.target}`}
              data-cursor="link"
              onClick={() => goTo(l.target)}
              className="group relative ama-label"
              style={{
                color: active === l.target ? "#1B3818" : "#000",
                transition: "color 400ms var(--ama-ease)",
              }}
            >
              {l.label}
              <span
                className="absolute -bottom-1.5 left-0 h-px bg-[#1B3818]"
                style={{
                  width: active === l.target ? "100%" : "0%",
                  transition: "width 450ms var(--ama-ease)",
                }}
              />
            </button>
          ))}
        </nav>
      </div>
    </motion.header>
  );
}
